import React, { useState } from 'react';
import { Save, Shield, Eye, EyeOff, Palette } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useToast } from '../ToastProvider';

// #312: Reiter „Servereinstellungen“ des Admin-Bereichs, Code unverändert
// aus AdminPanel.js übernommen (Muster und Begründung: admin/LogsTab.js).
//
// Die Allowlist lädt weiterhin `loadData()` im Panel zusammen mit den anderen
// Reitern und reicht sie über die Setter herein. `setSaving` ist der gemeinsame
// Speichern-Zustand des Panels. Der Kunden-Schalter lebt seit #520 im Reiter
// „Features“ (admin/FeaturesTab.js).
//
// #627: Die Allowlist kommt aus GET /api/admin/settings/allowed-emails. Bis
// dahin las das Panel sie aus /api/setup/status, das sie seit #46 nicht mehr
// liefert: Das Feld war leer, und "Speichern" haette die bestehende Liste in
// der .env durch den Feldinhalt ersetzt. Deshalb gilt jetzt: Erst wenn die
// Liste wirklich geladen ist, laesst sich speichern. Schlaegt das Laden fehl,
// zeigt der Reiter das an, statt ein leeres Feld anzubieten.
//
// #606: Die Darstellungs-Vorgabe der Installation (#565) steht hier und nicht
// mehr bei den Features, weil sie kein An/Aus-Schalter ist. Sie speichert
// SOFORT über den Hook des Features-Reiters (`featuresTab.handleSetFeature`),
// nicht über „Speichern“: Das schreibt die Allowlist in die `.env`, und ein
// Formular mit „Speichern“ schickt keine Schalter mit (#520).
export function useServerTab({ setError, setSaving }) {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [allowedEmails, setAllowedEmails] = useState('');
  const [originalAllowedEmails, setOriginalAllowedEmails] = useState('');
  const [showAllowedEmails, setShowAllowedEmails] = useState(false);
  // #627: 'laedt' | 'ok' | 'fehler' -- gespeichert wird nur bei 'ok'
  const [ladeStand, setLadeStand] = useState('laedt');

  // Antwort von apiService.getAllowedEmails() bzw. null, wenn sie scheiterte
  const uebernehmeGeladen = (antwort) => {
    if (antwort && typeof antwort.allowedEmails === 'string') {
      setAllowedEmails(antwort.allowedEmails);
      setOriginalAllowedEmails(antwort.allowedEmails);
      setLadeStand('ok');
    } else {
      setLadeStand('fehler');
    }
  };

  const handleSaveAllowedEmails = async () => {
    if (ladeStand !== 'ok') return;
    setSaving(true);
    setError(null);
    
    try {
      await apiService.updateAllowedEmails(allowedEmails);
      setOriginalAllowedEmails(allowedEmails);
      pushToast({ message: t('admin.server.allowedSaved', 'Erlaubte E-Mail-Adressen erfolgreich gespeichert.'), type: 'success' });
    } catch (error) {
      console.error('Error updating allowed emails:', error);
      setError(t('admin.server.allowedSaveError', 'Fehler beim Speichern der erlaubten E-Mail-Adressen.'));
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = allowedEmails !== originalAllowedEmails;

  return { allowedEmails, setAllowedEmails, setOriginalAllowedEmails, uebernehmeGeladen, ladeStand, showAllowedEmails, setShowAllowedEmails, hasUnsavedChanges, handleSaveAllowedEmails };
}

const ServerTab = ({ tab, saving, featuresTab }) => {
  const { t } = useI18n();
  const { allowedEmails, setAllowedEmails, ladeStand, showAllowedEmails, setShowAllowedEmails, hasUnsavedChanges, handleSaveAllowedEmails } = tab;
  const ladeFehler = ladeStand === 'fehler';

  return (
    <div className="admin-section">
      <h3>{t('admin.tabs.server', 'Servereinstellungen')}</h3>

      <div className="settings-group">
        <label className="settings-label">
          <Shield size={16} />
          {t('admin.server.allowedEmails', 'Erlaubte E-Mail-Adressen')}
        </label>
        <div className="settings-description">
          {t('admin.server.desc', 'Beschränken Sie den Zugang auf bestimmte E-Mail-Adressen oder Domains. Leer lassen für alle O365-Benutzer.')}
        </div>
        
        {ladeFehler && (
          <div className="alert alert-error" role="alert">
            {t('admin.server.allowedLoadError', 'Die erlaubten E-Mail-Adressen konnten nicht geladen werden. Speichern ist gesperrt, damit die bestehende Liste nicht überschrieben wird. Bitte die Seite neu laden.')}
          </div>
        )}

        <div className="allowed-emails-field">
          <div className="input-group">
            <textarea
              aria-label={t('admin.server.allowedEmails', 'Erlaubte E-Mail-Adressen')}
              disabled={ladeStand !== 'ok'}
              value={allowedEmails}
              onChange={(e) => setAllowedEmails(e.target.value)}
              placeholder={t('admin.server.allowedPlaceholder', 'Beispiele:\nuser1@example.com, user2@example.com\n*@example.com\nadmin@company.de, *@company.de')}
              rows="4"
              className="settings-textarea"
              style={{ 
                filter: showAllowedEmails ? 'none' : 'blur(4px)',
                transition: 'filter 0.2s'
              }}
            />
            <button
              type="button"
              className="visibility-toggle"
              onClick={() => setShowAllowedEmails(!showAllowedEmails)}
              title={showAllowedEmails ? t('admin.server.hide', 'Ausblenden') : t('admin.server.show', 'Anzeigen')}
            >
              {showAllowedEmails ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          
          <div className="settings-help">
            <strong>{t('admin.server.formatting', 'Formatierung:')}</strong>
            <ul>
              <li><code>user@example.com</code> - {t('admin.server.formatEmail', 'Exakte E-Mail-Adresse')}</li>
              <li><code>*@example.com</code> - {t('admin.server.formatDomain', 'Alle Adressen der Domain')}</li>
              <li>{t('admin.server.formatMultiple', 'Mehrere Adressen mit Komma trennen')}</li>
            </ul>
          </div>
        </div>
        
        <div className="settings-actions">
          <button
            onClick={handleSaveAllowedEmails}
            className={`btn btn-primary ${hasUnsavedChanges ? 'has-changes' : ''}`}
            disabled={saving || !hasUnsavedChanges || ladeStand !== 'ok'}
          >
            <Save size={16} />
            {saving ? t('common.saving', 'Speichere...') : t('common.save', 'Speichern')}
          </button>
          
          {hasUnsavedChanges && (
            <span className="unsaved-indicator">{t('admin.server.unsaved', 'Ungespeicherte Änderungen')}</span>
          )}
        </div>
      </div>

      {featuresTab?.features && (
        <div className="settings-group">
          <label className="settings-label" htmlFor="vorgabe-darstellung">
            <Palette size={16} />
            {t('admin.features.darstellung', 'Darstellung (Vorgabe)')}
          </label>
          <div className="settings-description">
            {t('admin.features.darstellungDesc', 'Erscheinungsbild für alle, die unter dem Zahnrad keine eigene Darstellung gewählt haben.')}
            {' '}{t('admin.server.darstellungSofort', 'Die Auswahl speichert sofort.')}
          </div>
          <select
            id="vorgabe-darstellung"
            className="settings-select"
            value={featuresTab.features.darstellung || 'modern'}
            disabled={featuresTab.speichert === 'darstellung'}
            onChange={(e) => featuresTab.handleSetFeature('darstellung', e.target.value)}
          >
            <option value="modern">{t('darstellung.modern', 'Modern')}</option>
            <option value="klassisch">{t('darstellung.klassisch', 'Klassisch')}</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default ServerTab;
